import { FC } from 'react';
import type { PaymentHistoryItem } from '@teachur/http-client';

const STATUS_STYLES: Record<string, string> = {
  paid: 'bg-green-50 text-green-700',
  succeeded: 'bg-green-50 text-green-700',
  open: 'bg-yellow-50 text-yellow-700',
  pending: 'bg-yellow-50 text-yellow-700',
  draft: 'bg-gray-50 text-gray-600',
  void: 'bg-red-50 text-red-600',
  failed: 'bg-red-50 text-red-600',
  uncollectible: 'bg-red-50 text-red-600',
};

const formatAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);

const formatDate = (timestamp: number) =>
  new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

interface InvoiceRowProps {
  payment: PaymentHistoryItem;
}

export const InvoiceRow: FC<InvoiceRowProps> = ({ payment }) => (
  <tr className="border-t border-solid border-(--neutral-white-NW600) hover:bg-gray-50 transition-colors">
    <td className="px-4 py-3 [font-family:var(--font-montserrat)] [font-size:13px] font-medium text-(--neutral-black-NB1000)">
      {payment.number ?? (payment.source === 'charge' ? 'One-time payment' : '—')}
    </td>
    <td className="px-4 py-3 [font-family:var(--font-montserrat)] [font-size:13px] text-(--neutral-black-NB700)">
      {formatDate(payment.created)}
    </td>
    <td className="px-4 py-3 [font-family:var(--font-montserrat)] [font-size:13px] text-(--neutral-black-NB700)">
      {payment.description || '—'}
    </td>
    <td className="px-4 py-3 [font-family:var(--font-montserrat)] [font-size:13px] font-medium text-(--neutral-black-NB1000) text-right">
      {formatAmount(payment.amountDue, payment.currency)}
    </td>
    <td className="px-4 py-3 text-center">
      <span
        className={`inline-block rounded-full px-2.5 py-0.5 [font-family:var(--font-montserrat)] [font-size:12px] font-medium capitalize ${STATUS_STYLES[payment.status ?? ''] || 'bg-gray-50 text-gray-600'}`}
      >
        {payment.status}
      </span>
    </td>
    <td className="px-4 py-3 text-right">
      <div className="flex items-center justify-end gap-2">
        {payment.hostedInvoiceUrl && (
          <a
            href={payment.hostedInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="[font-family:var(--font-montserrat)] [font-size:12px] font-medium text-[#FE8701] no-underline hover:underline"
          >
            View
          </a>
        )}
        {payment.invoicePdf && (
          <a
            href={payment.invoicePdf}
            target="_blank"
            rel="noopener noreferrer"
            className="[font-family:var(--font-montserrat)] [font-size:12px] font-medium text-[#FE8701] no-underline hover:underline"
          >
            PDF
          </a>
        )}
        {payment.receiptUrl && (
          <a
            href={payment.receiptUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="[font-family:var(--font-montserrat)] [font-size:12px] font-medium text-[#FE8701] no-underline hover:underline"
          >
            Receipt
          </a>
        )}
      </div>
    </td>
  </tr>
);
